<div class="space-y-6">
  <app-page-header
    title="Wallets"
    subtitle="Supervision financière des acteurs — comptes en dette + recherche ciblée."
  />

  <!-- Lookup card -->
  <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
    <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Consulter un wallet</h2>
    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
      Saisis le type d'acteur et son identifiant pour ouvrir son snapshot (CREDIT + BALANCE).
    </p>
    <form [formGroup]="lookupForm" (ngSubmit)="submitLookup()" class="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
      <div class="sm:w-48">
        <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Type</label>
        <app-pretty-select
          formControlName="ownerType"
          [options]="lookupOwnerTypeOptions"
        />
      </div>
      <div class="flex-1">
        <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Identifiant (ownerRef)</label>
        <input
          type="number"
          formControlName="ownerRef"
          placeholder="ex. 42"
          class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
        />
      </div>
      <button
        type="submit"
        [disabled]="lookupForm.invalid"
        class="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Voir le wallet
      </button>
    </form>
  </section>

  <!-- Debt list -->
  <section class="rounded-2xl border border-gray-200 bg-white shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
    <div class="border-b border-gray-200 px-5 py-4 dark:border-gray-800">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex flex-col gap-2">
          <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Comptes CREDIT en dette</h2>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            Acteurs dont la réserve CREDIT est négative — leur BALANCE est gelée jusqu'à remboursement.
          </p>
        </div>
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div class="w-48">
            <app-pretty-select
              [formControl]="ownerTypeCtrl"
              [options]="ownerTypeOptions"
              placeholder="Type d'acteur"
            />
          </div>
          <div class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>Par page</span>
            <div class="w-20">
              <app-pretty-select
                [options]="pageSizeOptions"
                [ngModel]="size()"
                (ngModelChange)="changeSize($event ?? 20)"
                placeholder="20"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    @if (error() || (loading() && items().length === 0) || items().length === 0) {
      <app-data-state
        [loading]="loading()"
        [error]="error()"
        [empty]="!loading() && !error() && items().length === 0"
        loadingMessage="Chargement des comptes en dette…"
        emptyMessage="Aucun {{ typeLabel[ownerTypeCtrl.value!] | lowercase }} n'est en dette. 🎉"
        (retry)="load()"
      />
    } @else {
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
          <thead class="bg-gray-50 dark:bg-white/[0.02]">
            <tr>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Acteur</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Compte CREDIT</th>
              <th class="px-5 py-3 text-right text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Solde</th>
              <th class="px-5 py-3 text-right text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Dette</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Depuis</th>
              <th class="px-5 py-3 text-right text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
            @for (row of items(); track row.accountId) {
              <tr class="transition hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                <td class="whitespace-nowrap px-5 py-4">
                  <a [routerLink]="['/wallets', row.ownerType, row.ownerRef]" class="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400">
                    {{ typeLabel[row.ownerType] }} #{{ row.ownerRef }}
                  </a>
                </td>
                <td class="whitespace-nowrap px-5 py-4 font-mono text-xs text-gray-700 dark:text-gray-300">
                  #{{ row.accountId }}
                </td>
                <td class="whitespace-nowrap px-5 py-4 text-right text-sm font-medium text-error-700 dark:text-error-400">
                  {{ row.balance | number:'1.0-0' }} <span class="text-xs font-normal text-gray-400">XOF</span>
                </td>
                <td class="whitespace-nowrap px-5 py-4 text-right text-sm font-semibold text-error-700 dark:text-error-400">
                  {{ row.debt | number:'1.0-0' }} <span class="text-xs font-normal text-gray-400">XOF</span>
                </td>
                <td class="whitespace-nowrap px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                  {{ row.sinceUpdatedAt | ldate:'dd/MM/yy HH:mm' }}
                </td>
                <td class="whitespace-nowrap px-5 py-4 text-right">
                  <a [routerLink]="['/wallets', row.ownerType, row.ownerRef]" class="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400">Voir</a>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      @if (totalPages() > 0) {
        <div class="flex flex-col gap-3 border-t border-gray-200 px-5 py-4 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between">
          <p class="text-xs text-gray-500 dark:text-gray-400">
            Page {{ page() }} sur {{ totalPages() }} — {{ totalItems() }} compte(s) en dette
          </p>
          <div class="flex flex-wrap items-center gap-1">
            <button type="button" (click)="goToPage(page() - 1)" [disabled]="page() <= 1" class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/5">Précédent</button>
            @for (n of pageWindow(); track n) {
              <button
                type="button"
                (click)="goToPage(n)"
                class="min-w-[36px] rounded-lg border px-3 py-1.5 text-xs font-medium transition"
                [class.border-brand-500]="n === page()"
                [class.bg-brand-500]="n === page()"
                [class.text-white]="n === page()"
                [class.border-gray-300]="n !== page()"
                [class.text-gray-700]="n !== page()"
                [class.hover:bg-gray-50]="n !== page()"
                [class.dark:border-gray-700]="n !== page()"
                [class.dark:text-gray-400]="n !== page()"
                [class.dark:hover:bg-white/5]="n !== page()"
              >
                {{ n }}
              </button>
            }
            <button type="button" (click)="goToPage(page() + 1)" [disabled]="page() >= totalPages()" class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/5">Suivant</button>
          </div>
        </div>
      }
    }
  </section>
</div>
